import { useState } from 'react'
import { Activity, ArrowRight, FileCheck, FileText, ShieldCheck, Trash2 } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { PageHeader } from '@/app/shell/PageHeader'
import { useControls, useDeleteSite, useFiles, useIndices, useObligations, useQuestions } from '@/lib/api/hooks'
import { riskColor, tok } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { MetricTile } from '@/ui/metric-tile'
import { PackBadge } from '@/ui/pack-badge'

export function SiteOverviewPage() {
  const { site, siteId } = useCurrentSite()
  const id = siteId ?? ''
  const nav = useNavigate()
  const { role } = useRole()
  const del = useDeleteSite()
  const [confirmDel, setConfirmDel] = useState(false)
  const indices = useIndices(id)
  const controls = useControls(id)
  const obligations = useObligations(id)
  const questions = useQuestions()
  const files = useFiles(id)

  const noIndices = indices.isError
  const degraded = noIndices && site?.pack === 'DSEAR' // petrochem: metadata in progress
  const noteCopy = degraded ? 'engine metadata in progress' : 'not assessed yet'
  const industry = site?.pack === 'DSEAR' ? 'petrochem' : 'BESS'
  const totalQ = (questions.data ?? []).filter((q) => q.industry === industry).length
  const answered = controls.data?.length ?? 0
  const coverage = totalQ ? Math.round((answered / totalQ) * 100) : 0
  const openObl = (obligations.data ?? []).filter((o) => o.status === 'open').length
  const cc = indices.data?.control_confidence_percent ?? 0

  const base = `/site/${id}`
  const cards: { icon: LucideIcon; title: string; stat: string; to: string }[] = [
    { icon: ShieldCheck, title: 'Underwriting', stat: noIndices ? noteCopy : `${indices.data?.loss_probability_label ?? '—'} loss probability`, to: `${base}/underwriting` },
    { icon: FileText, title: 'Assessment', stat: `${coverage}% coverage`, to: `${base}/assessment` },
    { icon: FileCheck, title: 'Evidence', stat: `${files.data?.length ?? 0} documents`, to: `${base}/evidence` },
    { icon: Activity, title: 'Operations', stat: 'live telemetry', to: `${base}/operations` },
  ]

  return (
    <>
      <PageHeader
        title={site?.name ?? 'Site'}
        subtitle={
          <>
            {site && <PackBadge pack={site.pack} />}
            <span>Site overview</span>
            {degraded && (
              <span className="rounded px-1.5 py-0.5 text-[11.5px] text-flare">engine metadata in progress</span>
            )}
          </>
        }
        actions={
          role === 'assessor' ? (
            <Button variant="destructive" size="sm" onClick={() => setConfirmDel(true)}>
              <Trash2 /> Delete site
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {noIndices ? (
          <div className="rounded-xl border bg-card p-4">
            <KLabel>Control confidence</KLabel>
            <div className="mt-2 font-display text-[29px] font-semibold text-faint">—</div>
            <div className="mt-3 text-[11.5px] text-faint">{noteCopy}</div>
          </div>
        ) : (
          <MetricTile label="Control confidence" value={String(cc)} unit="/100" tone={riskColor(cc)} state={indices.data?.maturity_band ?? undefined} onDrill={() => nav(`${base}/underwriting`)} />
        )}
        <MetricTile label="Coverage" value={`${coverage}%`} tone={riskColor(coverage)} state={`${answered}/${totalQ}`} onDrill={() => nav(`${base}/assessment`)} />
        <div className="rounded-xl border bg-card p-4">
          <KLabel>Open obligations</KLabel>
          <div className="mt-2 font-display text-[29px] font-semibold tabular" style={{ color: openObl > 0 ? tok('--flare') : tok('--stable') }}>
            {openObl}
          </div>
          <button onClick={() => nav(`${base}/obligations`)} className="mt-3 text-[11.5px] text-muted-foreground hover:text-foreground">
            view obligations →
          </button>
        </div>
        {noIndices ? (
          <div className="rounded-xl border bg-card p-4">
            <KLabel>Loss probability</KLabel>
            <div className="mt-2 font-display text-[29px] font-semibold text-faint">—</div>
            <div className="mt-3 text-[11.5px] text-faint">{noteCopy}</div>
          </div>
        ) : (
          <MetricTile
            label="Loss probability"
            value={`${indices.data?.loss_probability_percent ?? 0}%`}
            tone={riskColor(100 - (indices.data?.loss_probability_percent ?? 0))}
            state={indices.data?.loss_probability_label ?? undefined}
            onDrill={() => nav(`${base}/underwriting`)}
          />
        )}
      </div>

      <div className="mb-3 mt-6 font-display text-[13px] font-semibold">Site workspace</div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <button key={c.title} onClick={() => nav(c.to)} className="text-left">
            <Card className="transition-colors hover:border-primary">
              <div className="flex items-center justify-between">
                <c.icon className="size-5 text-primary" />
                <ArrowRight className="size-4 text-faint" />
              </div>
              <div className="mt-3 font-display text-[15px] font-semibold">{c.title}</div>
              <div className="mt-1 text-[12.5px] text-muted-foreground">{c.stat}</div>
            </Card>
          </button>
        ))}
      </div>

      {confirmDel && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setConfirmDel(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Delete site"
            className="w-full max-w-md rounded-xl border bg-popover p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-display text-[17px] font-semibold">Delete {site?.name}?</h2>
            <p className="mt-2 text-[13px] text-muted-foreground">
              This permanently removes the site and all its responses, evidence, mappings and obligations.
              This cannot be undone.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmDel(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(id, {
                    onSuccess: () => {
                      setConfirmDel(false)
                      nav('/portfolio')
                    },
                  })
                }
              >
                <Trash2 /> {del.isPending ? 'Deleting…' : 'Delete site'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
